import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Card,
  Checkbox,
  Flex,
  TextField,
  Title,
  Typography,
} from "@qws-org/ui-kit";
import { type FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type { AuthFeatureData } from "~/core";
import { Eye, EyeSlash } from "~/icons";
import type { LoginCredential } from "~/shared/utils/auth-poker-sdk/auth-poker-sdk";

import { createAuthValidationSchema } from "./auth.schema";

export type ServerErrors = string | null;

export type AuthData = ExtractArrayItemType<
  NonNullable<AuthFeatureData>["auth"]
>;
export type AuthorizationFormProps = {
  data: AuthData;
  serverError?: ServerErrors;
  onSubmit: (data: LoginCredential) => void;
  onForgotPassword: () => void;
};

export const AuthorizationForm: FC<AuthorizationFormProps> = ({
  data,
  onSubmit,
  serverError,
  onForgotPassword,
}) => {
  const {
    handleSubmit,
    control,
    setError,
    clearErrors,
    formState: { isValid, isSubmitting, submitCount },
  } = useForm<LoginCredential>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: { isRememberPassword: false },
    resolver: yupResolver(
      createAuthValidationSchema({
        validation_required: data?.validation_required ?? "Field is required!",
        validation_password_max_width:
          data?.validation_password_max_width ??
          "Must be at most 20 characters ",
        validation_password_min_width:
          data?.validation_password_min_width ??
          "Must be at least 5 characters",
      }),
    ),
  });
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    if (serverError) {
      setError("password", {
        type: "server",
        message: data?.validation_invalid_credentials ?? "Invalid credentials",
      });

      setError("nick", {
        type: "server",
        message: " ",
      });
    } else {
      clearErrors();
    }
  }, [serverError, setError, clearErrors, submitCount]);
  return (
    <Card
      variant="secondary"
      minWidth={312}
      maxWidth={440}
      minHeight={572}
      height={572}
      md={{ maxWidth: 525, minHeight: 640 }}
      className="rounded-2xl md:rounded-lg "
    >
      <form onSubmit={handleSubmit(onSubmit)} className="h-full">
        <Flex
          height="100%"
          p={{ px: 16, py: 24 }}
          md={{ p: { py: 32, px: 40 } }}
          gap={8}
          flexDirection="column"
        >
          <Title
            as="h2"
            size="small"
            weight="bold"
            horizontalAlign="center"
            p={{ pb: 16 }}
            md={{ p: { pb: 24, pt: 0 }, horizontalAlign: "left" }}
            data-testid="authorization-form-title"
          >
            {data?.form_title}
          </Title>

          <Controller
            control={control}
            name="nick"
            render={({ field, fieldState: { error } }) => (
              <TextField
                minHeight="fit-content"
                maxWidth={312}
                minWidth="100%"
                height="min-content"
                {...field}
                name="nick"
                label={data?.form_input_label_name ?? "Nickname/email"}
                errorMessage={error?.message}
                data-testid="authorization-form-login-field"
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field, fieldState: { error } }) => (
              <TextField
                minHeight={"fit-content"}
                maxWidth={312}
                minWidth="100%"
                height="min-content"
                type={isVisible ? "text" : "password"}
                {...field}
                name="password"
                label={data?.form_input_label_password ?? "Password"}
                errorMessage={error?.message}
                icon={
                  isVisible ? (
                    <EyeSlash
                      onClick={() => setIsVisible(!isVisible)}
                      className="cursor-pointer"
                      viewBox="0 0 24 24"
                    />
                  ) : (
                    <Eye
                      onClick={() => setIsVisible(!isVisible)}
                      className="cursor-pointer"
                      viewBox="0 0 24 24"
                    />
                  )
                }
                data-testid="authorization-form-password-field"
              />
            )}
          />

          <Flex
            justify="space-between"
            minHeight={"fit-content"}
            align="center"
            maxWidth={312}
            minWidth="100%"
            height="min-content"
          >
            <Controller
              control={control}
              name="isRememberPassword"
              rules={{ required: false }}
              render={({ field, fieldState: { error } }) => (
                <Checkbox
                  aria-label="label"
                  errorMessage={error?.message}
                  data-testid="authorization-form-remember-password-field"
                  {...field}
                >
                  <Typography
                    weight="normal"
                    size="small"
                    md={{ size: "medium" }}
                    as="span"
                  >
                    {data?.form_remember_checkbox_text ?? "Remember password"}
                  </Typography>
                </Checkbox>
              )}
            />

            <Typography
              onClick={onForgotPassword}
              as="span"
              weight="normal"
              size="small"
              md={{ size: "medium" }}
              className="underline cursor-pointer "
              data-testid="authorization-restore-password"
            >
              {data?.form_restore_link_text ?? "Forgot password?"}
            </Typography>
          </Flex>

          <Button
            type="submit"
            size="small"
            md={{
              fontSize: "button.desktop.medium",
              p: { py: 16 },
              m: { mt: "auto" },
            }}
            m={{ mt: "auto" }}
            p={{ py: 12 }}
            weight="semibold"
            width="100%"
            maxHeight="none"
            minHeight="none"
            height="fit-content"
            data-testid="authorization-form-submit"
            isDisabled={!isValid || isSubmitting}
          >
            {data?.form_submit_text}
          </Button>
        </Flex>
      </form>
    </Card>
  );
};
